import { useState } from "react";
import { billingApi } from "../../api/endpoints.js";
import type { Product } from "../../api/types.js";
import { ScreenHeader, Content } from "../../components/Layout.js";
import { Card } from "../../components/Card.js";
import { Button } from "../../components/Button.js";
import { ErrorBanner, InfoBanner } from "../../components/Form.js";
import { Loader } from "../../components/Loader.js";
import { useQuery, mensajeDeError } from "../../hooks/useQuery.js";
import { useT } from "../../i18n/index.js";
import { irACheckout } from "../billing/checkout.js";

/** Créditos del terapeuta: saldo de evaluaciones + compra de paquetes por pago
 *  único (Mercado Pago). El modelo unificado eliminó la suscripción/licencia. */
export function CreditsPage() {
  const t = useT();
  const creditos = useQuery(() => billingApi.misCreditos());
  const productos = useQuery(() => billingApi.productos());
  const [accionError, setAccionError] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  const backUrl = window.location.origin + "/terapeuta/creditos";
  const paquetes = (productos.data ?? []).filter((p) => p.modalidad === "paquete");

  async function comprar(p: Product) {
    setAccionError(null);
    setOcupado(true);
    try {
      await irACheckout(p.codigo, backUrl);
    } catch (e) {
      setAccionError(mensajeDeError(e));
      setOcupado(false);
    }
  }

  return (
    <>
      <ScreenHeader title={t("therapist.credits.title")} />
      <Content>
        {accionError ? <ErrorBanner message={accionError} /> : null}

        <Card>
          <h2>Evaluaciones</h2>
          {creditos.loading ? (
            <Loader label={t("common.loading")} />
          ) : creditos.error ? (
            <>
              <ErrorBanner message={creditos.error} />
              <Button variant="ghost" onClick={creditos.reload}>
                {t("common.retry")}
              </Button>
            </>
          ) : creditos.data ? (
            creditos.data.ilimitado ? (
              <InfoBanner tone="info">Tienes evaluaciones ilimitadas.</InfoBanner>
            ) : creditos.data.saldo <= 0 ? (
              <InfoBanner tone="warn">
                No te quedan evaluaciones. Compra un paquete para iniciar una nueva.
              </InfoBanner>
            ) : (
              <InfoBanner tone={creditos.data.saldo <= 2 ? "warn" : "info"}>
                Te quedan <strong>{creditos.data.saldo}</strong> evaluaciones
                {creditos.data.saldo <= 2 ? " — considera comprar más pronto." : "."}
              </InfoBanner>
            )
          ) : null}
        </Card>

        <InfoBanner>Los créditos se acreditan automáticamente al confirmarse el pago.</InfoBanner>

        <Card>
          <h2>Paquetes</h2>
          {productos.loading ? (
            <Loader label={t("common.loading")} />
          ) : productos.error ? (
            <>
              <ErrorBanner message={productos.error} />
              <Button variant="ghost" onClick={productos.reload}>
                {t("common.retry")}
              </Button>
            </>
          ) : paquetes.length > 0 ? (
            <ul style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 8 }}>
              {paquetes.map((p) => (
                <li
                  key={p.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "10px 12px",
                    border: "1px solid var(--line)",
                    borderRadius: "var(--radius)",
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 14 }}>{p.nombre}</div>
                    <div style={{ fontSize: 13, color: "var(--ink-soft)" }}>
                      {p.precio} {p.moneda} · {p.evaluacionesIncluidas ?? "∞"} evaluaciones
                      {p.vigenciaDias ? ` · vigencia ${p.vigenciaDias} días` : ""}
                    </div>
                  </div>
                  <Button disabled={ocupado} onClick={() => void comprar(p)}>
                    {t("therapist.credits.buy")}
                  </Button>
                </li>
              ))}
            </ul>
          ) : (
            <p>No hay paquetes disponibles por ahora.</p>
          )}
        </Card>

      </Content>
    </>
  );
}
